import axios from 'axios';
import JWTService from '@/services/JWTService';
import { appErrorHandler } from '@/plugins/response-handler';

const BASE_URL = process.env.VUE_APP_BASE_URL_V1;

const REFRESH_TOKEN_URL = `${BASE_URL}/oauth/refresh-token`;

const REDIRECT_URL = '/sign-in';

const APIService = axios.create({
  baseURL: BASE_URL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

APIService.interceptors.request.use(
  (config) => {
    const token = JWTService.getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
);

let isAlreadyFetchingAccessToken = false;
let subscribers = [];

function onAccessTokenFetched(accessToken) {
  subscribers = subscribers.filter((callback) => callback(accessToken));
}

function addSubscriber(callback) {
  subscribers.push(callback);
}

APIService.interceptors.response.use(
  (response) => {
    const rOptions = response.config.rOptions || {};
    const itemParse = rOptions.itemParse || ((data) => data);
    const ModelClass = rOptions.model;

    if (response.data.result_code && response.data.result_code !== 'FLSCS') {
      const error = new Error('Запрос был обработан не успешно');
      error.response = response;
      appErrorHandler(error, rOptions);
      throw error;
    }
    let pagination = null;
    const page = response.headers['x-pagination-current-page'];
    const pages = response.headers['x-pagination-page-count'];
    const total = response.headers['x-pagination-total-count'];
    if (total) {
      pagination = {
        page: Number(page),
        pages: Number(pages),
        total: Number(total),
      };
    }
    if (rOptions.many) {
      let items = response.data.data.map((i) => itemParse(i));
      if (rOptions.model) {
        items = items.map((i) => new ModelClass(i, rOptions.modelParams));
      }

      const hasNext = pagination && pagination.pages > pagination.page;

      return {
        items,
        pagination,

        // Load more pagination
        loadMore: hasNext && (() => {
          const original = response.config;
          const conf = { ...original, params: { ...original.params, page: pagination.page + 1 } };
          return APIService(conf);
        }),
      };
    }
    if (rOptions.single || rOptions.model) {
      let item = itemParse(response.data.data);
      if (rOptions.model) item = new ModelClass(item);
      return { item };
    }

    return { ...response, pagination };
  },
  (error) => {
    if (error?.response?.status === 401) {
      const { config, response: { status } } = error;
      const originalRequest = config;
      const refreshToken = JWTService.getRefreshToken();

      if (status === 401) {
        if (!isAlreadyFetchingAccessToken) {
          isAlreadyFetchingAccessToken = true;

          axios.post(REFRESH_TOKEN_URL,
            {
              refresh_token: refreshToken,
            },
            {
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${refreshToken}`,
              },
            })
            .then((r) => {
              JWTService.setToken(r.data.access_token);
              JWTService.setRefreshToken(r.data.refresh_token);
              isAlreadyFetchingAccessToken = false;
              onAccessTokenFetched(r.data.access_token);
            })
            .catch(() => {
              localStorage.removeItem('vuex-sdo');
              JWTService.clearTokens();
              // Refresh has failed - reject the original request
              window.location.href = REDIRECT_URL;
              throw error;
            });
        }

        return new Promise((resolve) => {
          addSubscriber((accessToken) => {
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            resolve(axios(originalRequest));
          });
        });
      }
      return Promise.reject(error);
    }

    appErrorHandler(error, error?.config?.rOptions);
    throw error;
  },
);

export default APIService;
